import { useState, useRef, useCallback } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { useAssetList, useFileUploader } from "../api/hooks";

const WORKFLOW_CYCLE = ["new", "setup", "working", "done"] as const;

const ICON_MAP: Record<string, string> = {
  "application/pdf": "PDF",
  "application/zip": "ZIP",
  "text/plain": "TXT",
  "text/markdown": "MD",
  "text/csv": "CSV",
  "application/json": "JSON",
  "image/png": "PNG",
  "image/jpeg": "JPG",
  "image/svg+xml": "SVG",
};

const VIEWABLE_TYPES = new Set([
  "application/pdf", "text/plain", "text/markdown", "text/csv", "text/html",
  "application/json", "image/png", "image/jpeg", "image/gif", "image/svg+xml", "image/webp",
]);

function fileIcon(contentType: string): string {
  return ICON_MAP[contentType] ?? contentType.split("/")[1]?.toUpperCase().slice(0, 4) ?? "FILE";
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

const WORKFLOW_FILTERS = [
  { value: undefined, label: "All" },
  { value: "new", label: "New" },
  { value: "working", label: "Working" },
  { value: "done", label: "Done" },
] as const;

export type AssetBadge = { label: string; href?: string };

export function AssetViewer({
  projectId,
  repositoryId,
  assetBadges,
}: {
  projectId?: Id<"projects">;
  repositoryId?: Id<"repositories">;
  assetBadges?: Record<string, AssetBadge>;
}) {
  const [workflowFilter, setWorkflowFilter] = useState<string | undefined>(undefined);
  const assetState = useAssetList({ projectId, repositoryId, workflowStatus: workflowFilter });
  const assets = assetState.status === "ready" ? assetState.data : undefined;

  return (
    <div className="space-y-4">
      <FileUploader repositoryId={repositoryId} projectId={projectId} />

      {/* Workflow status filter */}
      <div className="flex gap-1">
        {WORKFLOW_FILTERS.map((f) => (
          <button
            key={f.label}
            onClick={() => setWorkflowFilter(f.value)}
            className={`px-3 py-1 text-xs rounded-md border transition-colors ${
              workflowFilter === f.value
                ? "bg-indigo-500/15 border-indigo-500/30 text-indigo-300"
                : "bg-zinc-900 border-zinc-700 text-zinc-500 hover:text-zinc-300"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {assets === undefined ? (
        <div className="text-zinc-600 text-sm py-4">Loading assets...</div>
      ) : assets.length === 0 ? (
        <div className="text-center py-8 text-zinc-600">
          <p className="text-sm">{workflowFilter ? `No ${workflowFilter} assets` : "No assets yet"}</p>
          {!workflowFilter && (
            <p className="text-xs mt-1 text-zinc-700">
              Drop files above or use the <code className="bg-zinc-800 px-1 rounded">asset_request_upload</code> MCP tool
            </p>
          )}
        </div>
      ) : (
        <>
          {assets.filter((a) => a.type === "output").length > 0 && (
            <AssetGroup label="Output Assets" assets={assets.filter((a) => a.type === "output")} assetBadges={assetBadges} />
          )}
          {assets.filter((a) => a.type === "input").length > 0 && (
            <AssetGroup label="Input Assets" assets={assets.filter((a) => a.type === "input")} assetBadges={assetBadges} />
          )}
        </>
      )}
    </div>
  );
}

// --- Upload component ---

function FileUploader({
  repositoryId,
  projectId,
}: {
  repositoryId?: Id<"repositories">;
  projectId?: Id<"projects">;
}) {
  const { state, progress, error, busy, upload, reset } = useFileUploader({
    repositoryId,
    projectId,
    type: "input",
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = useCallback((fileList: FileList | null) => {
    if (!fileList) return;
    void upload(Array.from(fileList));
  }, [upload]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  return (
    <div className="space-y-2">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !busy && inputRef.current?.click()}
        className={`relative flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-6 cursor-pointer transition-colors ${
          busy
            ? "border-zinc-700 bg-zinc-900/50 cursor-wait"
            : dragOver
              ? "border-indigo-500 bg-indigo-500/5"
              : "border-zinc-700 hover:border-zinc-500 hover:bg-zinc-900/50"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          disabled={busy}
        />

        {busy ? (
          <>
            <svg className="w-6 h-6 text-indigo-400 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-sm text-zinc-400">{progress}</p>
          </>
        ) : state === "done" ? (
          <>
            <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
            </svg>
            <p className="text-sm text-green-400">{progress}</p>
          </>
        ) : (
          <>
            <svg className="w-6 h-6 text-zinc-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
            </svg>
            <p className="text-sm text-zinc-400">
              Drop files here or <span className="text-indigo-400">browse</span>
            </p>
            <p className="text-[11px] text-zinc-600">Any file type supported</p>
          </>
        )}
      </div>

      {state === "error" && (
        <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          <span className="text-red-400 shrink-0">&#x2717;</span>
          <p className="text-sm text-red-300">{error}</p>
          <button
            onClick={reset}
            className="ml-auto text-red-400/60 hover:text-red-300 shrink-0"
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
}

// --- Asset display ---

function AssetGroup({ label, assets, assetBadges }: { label: string; assets: any[]; assetBadges?: Record<string, AssetBadge> }) {
  const swapOrder = useMutation(api.assets.swapExecutionOrder);

  const moveUp = (index: number) => {
    if (index <= 0) return;
    void swapOrder({ assetIdA: assets[index]._id, assetIdB: assets[index - 1]._id });
  };
  const moveDown = (index: number) => {
    if (index >= assets.length - 1) return;
    void swapOrder({ assetIdA: assets[index]._id, assetIdB: assets[index + 1]._id });
  };

  return (
    <div>
      <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2">
        {label}
        <span className="ml-2 text-zinc-600">{assets.length}</span>
      </h3>
      <div className="space-y-1.5">
        {assets.map((asset, i) => (
          <AssetCard
            key={asset._id}
            asset={asset}
            badge={assetBadges?.[asset._id]}
            onMoveUp={i > 0 ? () => moveUp(i) : undefined}
            onMoveDown={i < assets.length - 1 ? () => moveDown(i) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

function AssetCard({ asset, badge, onMoveUp, onMoveDown }: { asset: any; badge?: AssetBadge; onMoveUp?: () => void; onMoveDown?: () => void }) {
  const [copied, setCopied] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const canView = VIEWABLE_TYPES.has(asset.contentType);
  const setWorkflowStatus = useMutation(api.assets.setWorkflowStatus);

  const changeStatus = async (status: string) => {
    setShowStatusMenu(false);
    await setWorkflowStatus({ assetId: asset._id, workflowStatus: status });
  };

  const copyUrl = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await navigator.clipboard.writeText(asset.cdnUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const download = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(asset.cdnUrl, "_blank");
  };

  const view = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(asset.cdnUrl, "_blank");
  };

  return (
    <div className="group flex items-center gap-2 sm:gap-3 bg-zinc-900 border border-zinc-800 rounded-lg px-2 sm:px-3 py-2.5 hover:border-zinc-600 hover:bg-zinc-800/80 transition-colors">
      {/* Reorder buttons */}
      <div className="flex flex-col shrink-0">
        <button
          onClick={(e) => { e.stopPropagation(); onMoveUp?.(); }}
          disabled={!onMoveUp}
          className={`p-0.5 rounded transition-colors ${onMoveUp ? "text-zinc-600 hover:text-zinc-200 hover:bg-zinc-700" : "text-zinc-800 cursor-default"}`}
          title="Move up"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
          </svg>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onMoveDown?.(); }}
          disabled={!onMoveDown}
          className={`p-0.5 rounded transition-colors ${onMoveDown ? "text-zinc-600 hover:text-zinc-200 hover:bg-zinc-700" : "text-zinc-800 cursor-default"}`}
          title="Move down"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
          </svg>
        </button>
      </div>
      <div className="shrink-0 w-9 h-9 rounded bg-zinc-800 border border-zinc-700 flex items-center justify-center text-[10px] font-bold text-zinc-400 group-hover:text-zinc-200 group-hover:border-zinc-600 transition-colors">
        {fileIcon(asset.contentType)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-zinc-200 truncate group-hover:text-white transition-colors">
          {asset.fileName}
        </p>
        <div className="flex items-center gap-2 text-[11px] text-zinc-600">
          {asset.executionOrder != null && (
            <span className="font-mono text-zinc-500">#{asset.executionOrder}</span>
          )}
          {asset.taskId && (
            <span className="font-mono bg-zinc-800 px-1 rounded">{asset.taskId}</span>
          )}
          {asset.sizeBytes && <span>{formatBytes(asset.sizeBytes)}</span>}
          <span>{timeAgo(asset.createdAt)}</span>
          {badge && (
            badge.href ? (
              <a
                href={badge.href}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-[10px] font-medium bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 rounded px-1.5 py-0.5 hover:bg-indigo-500/25 hover:text-indigo-200 transition-colors max-w-[160px]"
                title={`Go to ${badge.label}`}
              >
                <svg className="w-2.5 h-2.5 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
                </svg>
                <span className="truncate">{badge.label}</span>
              </a>
            ) : (
              <span className="inline-flex items-center text-[10px] font-medium bg-zinc-800 text-zinc-400 border border-zinc-700 rounded px-1.5 py-0.5 max-w-[160px] truncate">
                {badge.label}
              </span>
            )
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {asset.workflowStatus && (
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setShowStatusMenu(!showStatusMenu); }}
              className={`px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider cursor-pointer transition-all flex items-center gap-1.5 ${
                asset.workflowStatus === "new" ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/40 hover:bg-indigo-500/30" :
                asset.workflowStatus === "working" ? "bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30" :
                asset.workflowStatus === "done" ? "bg-green-500/20 text-green-300 border border-green-500/40 hover:bg-green-500/30" :
                "bg-zinc-800 text-zinc-500"
              }`}
            >
              {asset.workflowStatus}
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
              </svg>
            </button>
            {showStatusMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowStatusMenu(false)} />
                <div className="absolute right-0 top-full mt-1 z-50 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl py-1 min-w-[120px]">
                  {WORKFLOW_CYCLE.map((s) => (
                    <button
                      key={s}
                      onClick={(e) => { e.stopPropagation(); void changeStatus(s); }}
                      className={`w-full text-left px-3 py-1.5 text-xs font-medium uppercase tracking-wider transition-colors flex items-center gap-2 ${
                        asset.workflowStatus === s
                          ? "text-white bg-zinc-700"
                          : "text-zinc-400 hover:text-white hover:bg-zinc-700"
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${
                        s === "new" ? "bg-indigo-400" :
                        s === "working" ? "bg-amber-400" :
                        "bg-green-400"
                      }`} />
                      {s}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        {canView && (
          <button
            onClick={view}
            className="p-1.5 rounded text-zinc-600 hover:text-zinc-200 hover:bg-zinc-700 transition-colors"
            title="View"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
            </svg>
          </button>
        )}
        <button
          onClick={download}
          className="p-1.5 rounded text-zinc-600 hover:text-zinc-200 hover:bg-zinc-700 transition-colors"
          title="Download"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
        </button>
        <div className="relative">
          <button
            onClick={copyUrl}
            className="p-1.5 rounded text-zinc-600 hover:text-zinc-200 hover:bg-zinc-700 transition-colors"
            title="Copy URL"
          >
            {copied ? (
              <svg className="w-3.5 h-3.5 text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
              </svg>
            )}
          </button>
          {copied && (
            <span className="absolute -top-7 left-1/2 -translate-x-1/2 text-[10px] bg-zinc-700 text-green-400 px-1.5 py-0.5 rounded whitespace-nowrap">
              Copied!
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
